<template>
  <div>
    <template v-if="sent">
      <p v-if="error" class="text-center">
        {{ $t('contact_form.sent.error') }}
      </p>
      <p v-else class="text-center">{{ $t('contact_form.sent.success') }}</p>
    </template>

    <template v-else>
      <form v-editable="blok" class="w-full">
        <div class="mb-6">
          <TextInput
            v-model="state.customerNo"
            input-id="customer_no"
            :label="$t('contact_form.customer_no.label')"
            data-test-id="customer_no"
          />
        </div>

        <ValidatedInputGroupLegacy
          v-slot="{ inputId }"
          class="mb-4"
          :is-valid="isEmailValid"
          :message="$t('contact_form.email.error')"
        >
          <TextInput
            v-model="state.email"
            type="email"
            :appearance="isEmailValid ? 'default' : 'danger'"
            :label="$t('contact_form.email.label')"
            :has-error-message="!isEmailValid"
            :input-id="inputId"
            :required="true"
            data-test-id="email"
            @change="v.email.$touch()"
            @blur="v.email.$touch()"
          />
        </ValidatedInputGroupLegacy>

        <div v-if="!isFinland" class="relative mb-6 w-1/4">
          <SalutationSelect
            v-model="state.salutation"
            input-id="catalog_order_form_salutation"
            data-test-id="salutation_select"
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <ValidatedInputGroupLegacy
            v-slot="{ inputId }"
            :is-valid="isValidFirstName"
            :message="$t('contact_form.first_name.error')"
          >
            <TextInput
              v-model="state.firstName"
              :appearance="isValidFirstName ? 'default' : 'danger'"
              :label="$t('contact_form.first_name.label')"
              :has-error-message="!isValidFirstName"
              :input-id="inputId"
              :required="true"
              data-test-id="first_name"
              @change="v.firstName.$touch()"
              @blur="v.firstName.$touch()"
            />
          </ValidatedInputGroupLegacy>

          <ValidatedInputGroupLegacy
            v-slot="{ inputId }"
            :is-valid="isValidLastName"
            :message="$t('contact_form.last_name.error')"
          >
            <TextInput
              v-model="state.lastName"
              :appearance="isValidLastName ? 'default' : 'danger'"
              :label="$t('contact_form.last_name.label')"
              :has-error-message="!isValidLastName"
              :input-id="inputId"
              :required="true"
              data-test-id="last_name"
              @change="v.lastName.$touch()"
              @blur="v.lastName.$touch()"
            />
          </ValidatedInputGroupLegacy>
        </div>

        <div class="mb-6 grid grid-cols-2 gap-4">
          <TextInput
            id="street"
            v-model="state.street"
            :label="$t('contact_form.street.label')"
            input-id="street"
            data-test-id="street"
          />

          <TextInput
            id="house-no"
            v-model="state.houseNo"
            :label="$t('contact_form.house_no.label')"
            input-id="house_no"
            data-test-id="house_no"
          />
        </div>

        <TextInput
          id="additional-address"
          v-model="state.additionalAddress"
          class="mb-6"
          :label="$t('contact_form.additional_address.label')"
          input-id="additional_address"
          data-test-id="additional_address"
        />

        <div class="mb-6 grid grid-cols-2 gap-4">
          <TextInput
            id="zip"
            v-model="state.zip"
            :label="$t('contact_form.zip.label')"
            input-id="zip"
            data-test-id="zip"
          />

          <TextInput
            id="city"
            v-model="state.city"
            :label="$t('contact_form.city.label')"
            input-id="city"
            data-test-id="city"
          />
        </div>

        <ValidatedInputGroupLegacy
          class="mb-2"
          :is-valid="isValidSubject"
          :message="$t('contact_form.subject.error')"
          data-test-id="subject"
        >
          <select
            id="subject"
            v-model="state.subject"
            :aria-describedby="isValidSubject ? 'subject-error' : undefined"
            class="border-gray-300 peer w-1/4 appearance-none border bg-white px-4 py-3 text-base transition-all focus:border-gray-secondary focus:outline-none focus:ring-1 focus:ring-gray-secondary"
          >
            <option>{{ $t('contact_form.subject.order') }}</option>
            <option>{{ $t('contact_form.subject.homepage') }}</option>
            <option>{{ $t('contact_form.subject.delivery') }}</option>
            <option>{{ $t('contact_form.subject.personal_data') }}</option>
            <option>{{ $t('contact_form.subject.products') }}</option>
            <option>{{ $t('contact_form.subject.return') }}</option>
            <option>{{ $t('contact_form.subject.revocation') }}</option>
          </select>
          <label
            for="subject"
            class="pointer-events-none absolute -top-2 left-4 bg-white px-2 text-xs text-black transition-all duration-300 peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-base peer-placeholder-shown:text-black-60 peer-focus:-top-2 peer-focus:text-xs peer-focus:font-bold"
          >
            {{ $t('contact_form.subject.placeholder') }} *
          </label>
        </ValidatedInputGroupLegacy>

        <ValidatedInputGroupLegacy
          v-slot="{ inputId }"
          class="mb-4"
          :is-valid="isValidMessage"
          :message="$t('contact_form.message.error')"
        >
          <TextAreaInput
            v-model="state.message"
            :tag="'textarea'"
            :appearance="isValidMessage ? 'default' : 'danger'"
            :label="$t('contact_form.message.label')"
            :has-error-message="!isValidMessage"
            :input-id="inputId"
            :required="true"
            data-test-id="message"
            @change="v.message.$touch()"
            @blur="v.message.$touch()"
          />
        </ValidatedInputGroupLegacy>

        <p class="mb-4 text-xs text-gray-secondary">
          {{ $t('form_fields.mandatory_field_note') }}
        </p>

        <Checkbox
          id="newsletter"
          :checked="state.newsletter"
          :label="$t('contact_form.newsletter')"
          class="mb-4 mr-2"
          @change="state.newsletter = !state.newsletter"
        />

        <Button type="primary" :loading="fetching" @click="submit">
          {{ $t('contact_form.submit') }}
        </Button>
      </form>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import type { SbContactForm } from '~/cms/storyblok/types/storyblok'
import { scrollToFirstInputWithError } from '#layers/base/utils/forms'

defineProps<{ blok: SbContactForm }>()

const { fetch, fetching, error } = useContactForm()
const { isInList: isFinland } = useCountryCheck(['fi'])

const validSalutation = (value: string) => ['f', 'm'].includes(value)

const state = reactive({
  customerNo: '',
  salutation: 'f' as 'f' | 'm',
  firstName: '',
  lastName: '',
  street: '',
  houseNo: '',
  additionalAddress: '',
  zip: '',
  city: '',
  email: '',
  subject: '',
  message: '',
  newsletter: false,
})

const rules = {
  firstName: { required },
  lastName: { required },
  message: { required },
  email: { required, isValidEmail },
  subject: { required },
  salutation: { required, validSalutation },
}

const v = useVuelidate(rules, state, { $lazy: true })

const sent = ref(false)

const submit = async () => {
  await v.value.$validate()

  if (!v.value.$invalid) {
    fetch({
      title: state.salutation,
      customerNo: {
        firstPart: state.customerNo || '',
      },
      firstName: state.firstName,
      name: state.lastName,
      street: state.street,
      houseNo: state.houseNo,
      additionalAddress: state.additionalAddress,
      zip: state.zip,
      city: state.city,
      email: state.email,
      subject: state.subject,
      message: state.message,
      newsletter: state.newsletter,
      gRecaptchaResponse: 'NULL',
    })
      .then(() => {
        sent.value = true
        return undefined
      })
      .catch((err) => {
        console.error('Contact form submission failed:', err)
      })
  } else {
    scrollToFirstInputWithError()
  }
}

const isValidMessage = computed(() => !v.value.message.$invalid)
const isValidFirstName = computed(() => !v.value.firstName.$invalid)
const isValidLastName = computed(() => !v.value.lastName.$invalid)
const isEmailValid = computed(() => !v.value.email.$invalid)
const isValidSubject = computed(() => !v.value.subject.$invalid)
</script>
